import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth-store";
import {
  getMyAccountDisplayPrefs,
  DEFAULT_ACCOUNT_DISPLAY_PREFS,
  type ThemePreference,
} from "@/services/account-display-prefs-service";

/**
 * MYID-D2 (2026-05-24) — apply the user's preferred theme as the `.dark`
 * class on <html>. Tailwind v4 picks up the variant automatically; legacy
 * CSS that respects prefers-color-scheme still works because the OS
 * preference is unchanged.
 *
 * Source of truth: AccountDisplayPrefs.theme (DSC-1B sandman entity →
 * Firestore projection → TanStack Query cache). Values: 'light' | 'dark'
 * | 'system'. 'system' watches the matchMedia query and toggles on OS
 * preference change.
 *
 * Fallback chain when sandman is unreachable (e.g. cold load on a flaky
 * connection):
 *
 *   1. localStorage cache (carry-over from the previous session via the
 *      effect's `applyTheme` call — see useApplyThemeOnMount below)
 *   2. DEFAULT_ACCOUNT_DISPLAY_PREFS.theme (== 'system')
 *
 * Why a separate hook vs folding into useApplyA11yOnMount: theme value
 * is server-owned (sandman), a11y is device-local. Different sources,
 * different change semantics.
 */

const STORAGE_KEY = "myCollabkareId:theme";

function readCachedTheme(): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_ACCOUNT_DISPLAY_PREFS.theme;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* ignore */
  }
  return DEFAULT_ACCOUNT_DISPLAY_PREFS.theme;
}

function writeCachedTheme(theme: ThemePreference): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

/**
 * Resolves the effective theme (light|dark) given a user preference. For
 * 'system', queries prefers-color-scheme.
 */
function resolveEffectiveTheme(pref: ThemePreference): "light" | "dark" {
  if (pref === "light" || pref === "dark") return pref;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(pref: ThemePreference): void {
  if (typeof document === "undefined") return;
  const effective = resolveEffectiveTheme(pref);
  document.documentElement.classList.toggle("dark", effective === "dark");
  document.documentElement.setAttribute("data-theme", effective);
}

/**
 * Apply-on-mount: reads cached theme synchronously (no flash of wrong
 * theme), then subscribes to:
 *   - TanStack Query refresh of AccountDisplayPrefs (canonical sandman value)
 *   - prefers-color-scheme change (only matters when pref==='system')
 *   - cross-tab updates via the storage event
 *
 * Mount this once in the App root. No JSX, no provider.
 */
export function useApplyThemeOnMount(): void {
  // Apply cached value immediately on first render so the very first
  // paint matches the user's expectation. No flicker between "light by
  // default" → "dark after sandman responds".
  const [pref, setPref] = useState<ThemePreference>(() => {
    const cached = readCachedTheme();
    applyTheme(cached);
    return cached;
  });

  // MYID-D2 HOTFIX (2026-05-25) — gate the canonical fetch on auth state.
  // Without this, useApplyThemeOnMount fires above AuthGuard and hammers
  // sandman with unauthenticated GETs during the KC redirect cycle
  // (97-call 401 storm captured in 2026-05-25 HAR — also tripped KC's
  // brute-force IP block in the harness). Once authenticated, the query
  // runs normally and feeds the dark/light class on <html>.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  // Canonical value from sandman (DSC-1B). Refreshes the cache + applied
  // class on first successful fetch. Stays cheap because TanStack Query
  // dedupes across hooks.
  const { data } = useQuery({
    queryKey: ["accountDisplayPrefs"],
    queryFn: getMyAccountDisplayPrefs,
    staleTime: 60_000,
    enabled: isInitialized && isAuthenticated,
    // MYID-D4 — persist so cold-load paints from cache while we refetch.
    // Tiny payload, high read frequency, low write frequency — perfect fit.
    meta: { persist: true },
  });

  useEffect(() => {
    if (!data) return;
    const canonical = data.theme || DEFAULT_ACCOUNT_DISPLAY_PREFS.theme;
    if (canonical !== pref) {
      setPref(canonical);
      writeCachedTheme(canonical);
      applyTheme(canonical);
    }
  }, [data, pref]);

  // matchMedia listener — only meaningful when pref === 'system'.
  useEffect(() => {
    if (pref !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  // Cross-tab sync via storage event.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const next = e.newValue as ThemePreference;
        if (next === "light" || next === "dark" || next === "system") {
          setPref(next);
          applyTheme(next);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
}

/**
 * Read-only access for components that want to show the resolved theme
 * (e.g. a theme-aware icon). Returns the effective theme (always
 * 'light' or 'dark' — 'system' is resolved through matchMedia).
 */
export function useEffectiveTheme(): "light" | "dark" {
  const [effective, setEffective] = useState<"light" | "dark">(() =>
    resolveEffectiveTheme(readCachedTheme()),
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setEffective(resolveEffectiveTheme(readCachedTheme()));
    mq.addEventListener("change", update);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) update();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      mq.removeEventListener("change", update);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return effective;
}

/**
 * Update the cached theme — called by AccountDisplayPrefs page after a
 * successful PUT. The sandman fetch will eventually overwrite this with
 * the canonical value, but writing immediately means the user sees
 * their choice applied without waiting on a refetch.
 */
export function cacheThemeLocally(theme: ThemePreference): void {
  writeCachedTheme(theme);
  applyTheme(theme);
}
